import { isSabHandle, SabMessageChannel, } from '@streamr/tv-engine';
import { Settings } from '@streamr/tv-engine/~/settings';
import { proxy, transfer, wrap } from 'comlink';
import { EventEmitter } from 'eventemitter3';
import { getRemoteLogPageConfig } from '../../utils/remoteLogger';
import { createWorker } from '../../utils/workerUtils';
import { AudioFrameExtractor } from './AudioFrameExtractor';
import { AudioTrackFanout } from './AudioTrackFanout';
import { createAudioDecoder } from './createAudioDecoder';
import { createAudioFrameExtractor } from './createAudioFrameExtractor';
import { isGapDiagnosticsEnabled } from './GapDiagnostics';
import { IncomingTrack } from './IncomingTrack';
import { AudioPlayer } from './multiaudioplayer/AudioPlayer';
import { perfWorkerName } from './perfFlags';
import { Timekeeper } from './Timekeeper';
/**
 * Wraps an extractor output track, applying a gain multiplier to every
 * audio frame's Float32Array samples before forwarding to the encoder.
 * Preserves timestamps exactly so the encoder FIFO stays in sync.
 */
class GainApplyingTrack {
    source;
    getGain;
    constructor(source, getGain) {
        this.source = source;
        this.getGain = getGain;
    }
    getTrackId() {
        return this.source.getTrackId();
    }
    isLocal() {
        return this.source.isLocal();
    }
    setListener(listener) {
        this.source.setListener(async (frame, done) => {
            if (frame) {
                const gain = this.getGain();
                if (gain !== 1) {
                    // Scale in place: the extractor emits a fresh private
                    // buffer per frame with this listener as its only
                    // consumer, so mutating it is exactly as safe as the
                    // gain === 1 passthrough below. Saves a Float32Array
                    // allocation + copy per frame (~100/s while unmuted).
                    const data = frame.data;
                    for (let i = 0; i < data.length; i++) {
                        data[i] *= gain;
                    }
                }
            }
            await listener(frame, done);
        });
    }
    unsetListener() {
        this.source.unsetListener();
    }
    hasListener() {
        return this.source.hasListener();
    }
    getBufferLength() {
        return this.source.getBufferLength();
    }
}
export class AudioEngine {
    LOG_PREFIX = '[AudioEngine]';
    audioFrameExtractors = new Map();
    audioDecoders = new Map();
    pendingTrackIds = new Set();
    workerDecoderTrackIds = new Set();
    audioContext;
    audioPlayer;
    incomingTracks = new Map();
    encodedAudioOutputTrackAddedCallback;
    // Composite audio capture state (extractors live on main thread)
    compositeAudioExtractor;
    /** one consumer track per composite encoder role on the mixed capture */
    compositeAudioFanout;
    // Original input tracks so they can be mixed into the composite capture
    inputAudioTracks = new Map();
    // Per-input-track gain multiplier (0.0–1.0)
    inputTrackGains = new Map();
    perTrackEncodingEnabled = false;
    // On-demand audio encoder worker
    audioEncoderWorkerRaw;
    audioEncoderWorkerProxy;
    // On-demand audio decoder worker
    audioDecoderWorkerRaw;
    audioDecoderWorkerProxy;
    referenceTimeEmitter = new EventEmitter();
    constructor(audioContext) {
        this.audioContext = audioContext;
    }
    setPerTrackEncodingEnabled(enabled) {
        this.perTrackEncodingEnabled = enabled;
    }
    async init(getOptimalDelayCallback, encodedAudioOutputTrackAddedCallback) {
        this.encodedAudioOutputTrackAddedCallback =
            encodedAudioOutputTrackAddedCallback;
        await AudioFrameExtractor.preloadModule(this.audioContext);
        await AudioFrameExtractor.initialize(this.audioContext);
        // Only pre-warm the worklet on browsers that use AudioWorklet
        // for extraction (WebKit/Safari). Chrome uses
        // MediaStreamTrackProcessor and doesn't need this.
        if (typeof MediaStreamTrackProcessor === 'undefined') {
            AudioFrameExtractor.preWarmWorklet();
        }
        this.audioPlayer = new AudioPlayer(this.audioContext, getOptimalDelayCallback);
        this.audioPlayer.on('newReferenceTimes', (trackId, presentationTimeMicros, realTimeMillis) => {
            this.referenceTimeEmitter.emit('newReferenceTimes', trackId, presentationTimeMicros, realTimeMillis);
        });
        this.audioPlayer.on('playbackStarted', (trackId) => {
            this.referenceTimeEmitter.emit('playbackStarted', trackId);
        });
        this.audioPlayer.on('audioUnderflow', (trackId) => {
            this.reportAudioUnderflow(trackId).catch(console.error);
            this.referenceTimeEmitter.emit('audioUnderflow', trackId);
        });
        this.audioPlayer.on('audioSyncSabReady', (trackId, handle) => {
            this.referenceTimeEmitter.emit('audioSyncSabReady', trackId, handle);
        });
        await this.audioPlayer.init();
    }
    async destroy() {
        console.log(`${this.LOG_PREFIX} destroy`);
        await this.stopAllN1Encoders();
        for (const [trackId, extractor] of this.audioFrameExtractors) {
            console.log(`${this.LOG_PREFIX} stopping extractor`, {
                trackId: trackId.slice(0, 8),
            });
            await extractor.stop();
        }
        this.audioFrameExtractors.clear();
        if (this.audioEncoderWorkerProxy) {
            await this.stopAudioEncoderWorker();
        }
        if (this.audioDecoderWorkerProxy) {
            await this.stopAudioDecoderWorker();
        }
        for (const [trackId, decoder] of this.audioDecoders) {
            console.log(`${this.LOG_PREFIX} destroying decoder`, {
                trackId: trackId.slice(0, 8),
            });
            await decoder.destroy();
        }
        this.audioDecoders.clear();
        this.workerDecoderTrackIds.clear();
        this.incomingTracks.clear();
        this.pendingTrackIds.clear();
        this.inputAudioTracks.clear();
        this.inputTrackGains.clear();
        await this.audioPlayer?.dispose();
        this.audioPlayer = undefined;
        await AudioFrameExtractor.teardown();
        console.log(`${this.LOG_PREFIX} destroy complete`);
    }
    // ========== Audio encoder worker lifecycle ==========
    async startAudioEncoderWorker() {
        if (this.audioEncoderWorkerProxy) {
            console.warn(`${this.LOG_PREFIX} audioEncoderWorker already running`);
            return;
        }
        this.audioEncoderWorkerRaw = this.createAudioEncoderWorker();
        this.audioEncoderWorkerProxy = wrap(this.audioEncoderWorkerRaw);
        const encoderRemoteLogPageConfig = getRemoteLogPageConfig();
        await this.audioEncoderWorkerProxy.init(proxy(async (encodedTrack) => {
            this.encodedAudioOutputTrackAddedCallback?.(encodedTrack);
        }), isGapDiagnosticsEnabled(), encoderRemoteLogPageConfig
            ? {
                ...encoderRemoteLogPageConfig,
                source: 'iPhone:audioEncoderWorker',
            }
            : null);
        console.log(`${this.LOG_PREFIX} audioEncoderWorker started`);
    }
    async stopAudioEncoderWorker() {
        try {
            await this.audioEncoderWorkerProxy?.destroy();
        }
        catch (error) {
            console.warn(`${this.LOG_PREFIX} audioEncoderWorker destroy failed`, error);
        }
        this.audioEncoderWorkerRaw?.terminate();
        this.audioEncoderWorkerProxy = undefined;
        this.audioEncoderWorkerRaw = undefined;
        console.log(`${this.LOG_PREFIX} audioEncoderWorker stopped`);
    }
    isAudioEncoderWorkerRunning() {
        return this.audioEncoderWorkerProxy !== undefined;
    }
    async connectEncoderNetworkingPort(handle) {
        if (!this.audioEncoderWorkerProxy) {
            throw new Error('AudioEncoderWorker not running');
        }
        if (isSabHandle(handle)) {
            await this.audioEncoderWorkerProxy.connectNetworkingPort(handle);
        }
        else {
            await this.audioEncoderWorkerProxy.connectNetworkingPort(transfer(handle, [handle]));
        }
    }
    async connectCompositeNetworkingPort(handle) {
        if (!this.audioEncoderWorkerProxy) {
            throw new Error('AudioEncoderWorker not running');
        }
        if (isSabHandle(handle)) {
            await this.audioEncoderWorkerProxy.connectCompositeNetworkingPort(handle);
        }
        else {
            await this.audioEncoderWorkerProxy.connectCompositeNetworkingPort(transfer(handle, [handle]));
        }
    }
    async connectCompositeLowNetworkingPort(handle) {
        if (!this.audioEncoderWorkerProxy) {
            throw new Error('AudioEncoderWorker not running');
        }
        if (isSabHandle(handle)) {
            await this.audioEncoderWorkerProxy.connectCompositeLowNetworkingPort(handle);
        }
        else {
            await this.audioEncoderWorkerProxy.connectCompositeLowNetworkingPort(transfer(handle, [handle]));
        }
    }
    async connectDecoderNetworkingPort(handle) {
        if (!this.audioDecoderWorkerProxy) {
            throw new Error('AudioDecoderWorker not running');
        }
        if (isSabHandle(handle)) {
            await this.audioDecoderWorkerProxy.connectNetworkingPort(handle);
        }
        else {
            await this.audioDecoderWorkerProxy.connectNetworkingPort(transfer(handle, [handle]));
        }
    }
    getAudioEncoderWorkerRaw() {
        return this.audioEncoderWorkerRaw;
    }
    // ========== Audio decoder worker lifecycle ==========
    async startAudioDecoderWorker() {
        if (this.audioDecoderWorkerProxy) {
            console.warn(`${this.LOG_PREFIX} audioDecoderWorker already running`);
            return;
        }
        this.audioDecoderWorkerRaw = this.createAudioDecoderWorker();
        this.audioDecoderWorkerProxy = wrap(this.audioDecoderWorkerRaw);
        const remoteLogPageConfig = getRemoteLogPageConfig();
        const remoteLog = remoteLogPageConfig
            ? {
                ...remoteLogPageConfig,
                source: 'iPhone:audioDecoderWorker',
            }
            : null;
        await this.audioDecoderWorkerProxy.init(proxy((trackId, _codec) => {
            this.handleNewDecoderTrack(trackId);
        }), proxy((trackId) => {
            this.handleDecoderTrackDone(trackId);
        }), proxy((trackId, optimalDelay) => {
            this.referenceTimeEmitter.emit('audioOptimalDelay', trackId, optimalDelay);
        }), isGapDiagnosticsEnabled(), remoteLog);
        console.log(`${this.LOG_PREFIX} audioDecoderWorker started`);
    }
    async reportAudioUnderflow(trackId) {
        await this.audioDecoderWorkerProxy?.reportUnderflow(trackId);
    }
    async stopAudioDecoderWorker() {
        try {
            await this.audioDecoderWorkerProxy?.destroy();
        }
        catch (error) {
            console.warn(`${this.LOG_PREFIX} audioDecoderWorker destroy failed`, error);
        }
        this.audioDecoderWorkerRaw?.terminate();
        this.audioDecoderWorkerProxy = undefined;
        this.audioDecoderWorkerRaw = undefined;
        console.log(`${this.LOG_PREFIX} audioDecoderWorker stopped`);
    }
    isAudioDecoderWorkerRunning() {
        return this.audioDecoderWorkerProxy !== undefined;
    }
    getAudioDecoderWorkerRaw() {
        return this.audioDecoderWorkerRaw;
    }
    /**
     * Called by AudioDecoderWorker when a new track is discovered.
     * Creates an AudioWorkletNode + MessageChannel and wires the decoder port.
     */
    handleNewDecoderTrack(trackId) {
        if (!this.audioPlayer)
            return;
        if (this.workerDecoderTrackIds.has(trackId)) {
            return;
        }
        this.workerDecoderTrackIds.add(trackId);
        console.log(`${this.LOG_PREFIX} handleNewDecoderTrack`, {
            trackId: trackId.slice(0, 16),
            hasPlayer: !!this.audioPlayer,
        });
        try {
            const { decoderPort } = this.audioPlayer.createTrackForDecoderPort(trackId);
            console.log(`${this.LOG_PREFIX} handleNewDecoderTrack: connecting track port`, {
                trackId: trackId.slice(0, 16),
                portKind: decoderPort instanceof MessagePort
                    ? 'MessagePort'
                    : 'SAB',
            });
            if (decoderPort instanceof MessagePort) {
                this.audioDecoderWorkerProxy?.connectTrackPort(trackId, transfer(decoderPort, [decoderPort]));
            }
            else {
                this.audioDecoderWorkerProxy?.connectTrackPort(trackId, decoderPort);
            }
        }
        catch (e) {
            console.error(`${this.LOG_PREFIX} handleNewDecoderTrack ERROR`, e);
            throw e;
        }
    }
    /**
     * Called by AudioDecoderWorker when a track is done.
     */
    handleDecoderTrackDone(trackId) {
        console.log(`${this.LOG_PREFIX} decoder track done`, {
            trackId: trackId.slice(0, 8),
        });
        this.workerDecoderTrackIds.delete(trackId);
        this.audioPlayer?.removeTrack(trackId);
    }
    /**
     * Update optimal delay for a decoder-managed track's worklet.
     */
    updateDecoderTrackOptimalDelay(trackId) {
        this.audioPlayer?.updateTrackOptimalDelay(trackId);
    }
    // ========== Per-track audio (extractor on main thread, encoder in worker) ==========
    async addMediaStreamAudioTrack(track) {
        const trackId = track.id;
        console.log(`${this.LOG_PREFIX} addMediaStreamAudioTrack`, {
            trackId: trackId.slice(0, 8),
            hasEncoderWorker: !!this.audioEncoderWorkerProxy,
        });
        if (this.audioFrameExtractors.has(trackId) ||
            this.pendingTrackIds.has(trackId)) {
            console.log(`${this.LOG_PREFIX} Skipping duplicate audio track`, {
                trackId: trackId.slice(0, 8),
            });
            return;
        }
        this.pendingTrackIds.add(trackId);
        let workletSabHandle;
        let encoderSabHandle;
        let timestampOffsetMicros;
        if (this.perTrackEncodingEnabled &&
            this.audioEncoderWorkerProxy &&
            typeof MediaStreamTrackProcessor === 'undefined' &&
            typeof SharedArrayBuffer !== 'undefined') {
            const trackTag = (track.id ?? 'unknown').slice(0, 8);
            const ch = new SabMessageChannel({
                capacity: 256 * 1024,
                name: `worklet<->audio-enc[${trackTag}]`,
            });
            workletSabHandle = ch.port1.getHandle();
            encoderSabHandle = ch.port2.getHandle();
            timestampOffsetMicros = Timekeeper.getInstance('producer:individual').getLastTimestampMicros();
        }
        let audioFrameExtractor;
        try {
            // Reuse the pre-warmed worklet (running since init()) to
            // avoid the ~500-800ms WebKit audio session reconfiguration
            // freeze that happens when a NEW AudioWorkletNode is created.
            const preWarmed = AudioFrameExtractor.claimPreWarmed();
            if (preWarmed) {
                // Send SAB handle to the running worklet and WAIT for
                // the worklet to confirm it has connected the SAB port
                // before attaching the mic source.
                await preWarmed.bindTrack(track, workletSabHandle);
                audioFrameExtractor = preWarmed;
                console.log(`${this.LOG_PREFIX} using pre-warmed AudioFrameExtractor`, { trackId: trackId.slice(0, 8) });
            }
            else {
                audioFrameExtractor = createAudioFrameExtractor(track, workletSabHandle);
                if (audioFrameExtractor instanceof AudioFrameExtractor) {
                    await AudioFrameExtractor.initialize(this.audioContext);
                    audioFrameExtractor.prepareWorklet();
                }
            }
        }
        catch (error) {
            this.pendingTrackIds.delete(trackId);
            throw error;
        }
        const rawOutputTrack = audioFrameExtractor.getOutputTrack();
        const gainAppliedTrack = new GainApplyingTrack(rawOutputTrack, () => this.inputTrackGains.get(trackId) ?? 1);
        this.audioFrameExtractors.set(trackId, audioFrameExtractor);
        this.pendingTrackIds.delete(trackId);
        this.inputAudioTracks.set(trackId, track);
        if (this.compositeAudioExtractor && this.audioPlayer) {
            this.audioPlayer.addInputSourceToCapture(track);
        }
        const tStartup0 = performance.now();
        if (this.perTrackEncodingEnabled &&
            this.audioEncoderWorkerProxy &&
            encoderSabHandle) {
            await this.audioEncoderWorkerProxy.connectWorkletPort(encoderSabHandle, trackId, timestampOffsetMicros ?? 0n, 0);
        }
        const tStartup1 = performance.now();
        if (this.perTrackEncodingEnabled && this.audioEncoderWorkerProxy) {
            await this.audioEncoderWorkerProxy.addPerTrackEncoder(proxy(gainAppliedTrack), trackId);
        }
        const tStartup2 = performance.now();
        audioFrameExtractor.start();
        const tStartup3 = performance.now();
        console.log(`${this.LOG_PREFIX} addMediaStreamAudioTrack startup timing — ` +
            `connectWorkletPort: ${(tStartup1 - tStartup0).toFixed(1)}ms, ` +
            `addPerTrackEncoder: ${(tStartup2 - tStartup1).toFixed(1)}ms, ` +
            `extractor.start: ${(tStartup3 - tStartup2).toFixed(1)}ms, ` +
            `total: ${(tStartup3 - tStartup0).toFixed(1)}ms`);
    }
    async removeMediaStreamAudioTrack(track) {
        const trackId = track.id;
        const audioFrameExtractor = this.audioFrameExtractors.get(trackId);
        if (audioFrameExtractor) {
            await audioFrameExtractor.stop();
            this.audioFrameExtractors.delete(trackId);
        }
        if (this.audioEncoderWorkerProxy) {
            await this.audioEncoderWorkerProxy.disconnectWorkletPort(trackId);
            await this.audioEncoderWorkerProxy.removePerTrackEncoder(trackId);
        }
        this.inputAudioTracks.delete(trackId);
        this.inputTrackGains.delete(trackId);
        this.audioPlayer?.removeInputSourceFromCapture(trackId);
    }
    // ========== Composite audio (extractor on main thread, encoder in worker) ==========
    /**
     * Start capturing mixed audio. Creates the composite extractor but does
     * NOT create encoders. Use startCompositeEncoder / stopCompositeEncoder
     * to manage individual encoders.
     */
    async startMixedAudioCapture() {
        if (this.compositeAudioExtractor) {
            console.warn(`${this.LOG_PREFIX} Mixed audio capture already started`);
            return;
        }
        const audioMediaTrack = this.audioPlayer?.startAudioCapture();
        if (!audioMediaTrack) {
            throw new Error('AudioPlayer not initialized');
        }
        for (const [, track] of this.inputAudioTracks) {
            this.audioPlayer?.addInputSourceToCapture(track);
        }
        this.compositeAudioExtractor =
            createAudioFrameExtractor(audioMediaTrack);
        if (this.compositeAudioExtractor instanceof AudioFrameExtractor) {
            await AudioFrameExtractor.initialize(this.audioContext);
        }
        this.compositeAudioExtractor.start();
        this.compositeAudioFanout = new AudioTrackFanout(this.compositeAudioExtractor.getOutputTrack(), 'composite-audio-mix');
        console.log(`${this.LOG_PREFIX} mixed audio capture started`);
    }
    /**
     * Add a composite encoder for a given role. The encoder runs in the
     * AudioEncoderWorker and encodes the mixed audio capture output.
     */
    async startCompositeEncoder(role, codec, bitrate, channels, callback) {
        if (!this.audioEncoderWorkerProxy) {
            throw new Error('audioEncoderWorker must be running');
        }
        if (!this.compositeAudioExtractor) {
            throw new Error('Mixed audio capture must be started first (call startMixedAudioCapture)');
        }
        console.log(`${this.LOG_PREFIX} startCompositeEncoder`, {
            role,
            codec,
            hasExtractor: !!this.compositeAudioExtractor,
        });
        // every role gets its own consumer track (a Track has one listener)
        if (!this.compositeAudioFanout) {
            this.compositeAudioFanout = new AudioTrackFanout(this.compositeAudioExtractor.getOutputTrack(), 'composite-audio-mix');
        }
        const inputTrack = this.compositeAudioFanout.add(role);
        await this.audioEncoderWorkerProxy.addCompositeEncoder(role, codec, bitrate, channels, proxy(inputTrack), proxy(callback));
    }
    /**
     * Remove a composite encoder by role.
     */
    async stopCompositeEncoder(role) {
        await this.audioEncoderWorkerProxy?.removeCompositeEncoder(role);
        this.compositeAudioFanout?.remove(role);
    }
    /**
     * Stop capturing the mixed audio output and clean up.
     */
    async stopMixedAudioCapture() {
        const hadExtractor = !!this.compositeAudioExtractor;
        this.audioPlayer?.stopAudioCapture();
        this.compositeAudioFanout?.destroy();
        this.compositeAudioFanout = undefined;
        if (this.compositeAudioExtractor) {
            this.compositeAudioExtractor.stop();
            this.compositeAudioExtractor = undefined;
        }
        console.log(`${this.LOG_PREFIX} mixed audio capture stopped`, {
            hadExtractor,
        });
    }
    // ========== N-1 audio (per-guest mix excluding their own tracks) ==========
    /** N-1 capture extractors, keyed by capture id (the guest's user id) */
    n1Extractors = new Map();
    /**
     * Start an N-1 encoder for `captureId` that mixes all audio except the
     * given inbound tracks and delivers encoded frames to the callback. The
     * exclusion set follows the guest's tracks through setN1ExcludedTracks.
     */
    async startN1Encoder(captureId, excludedTrackIds, codec, bitrate, channels, callback) {
        if (!this.audioEncoderWorkerProxy) {
            throw new Error('audioEncoderWorker must be running');
        }
        if (!this.audioPlayer) {
            throw new Error('AudioPlayer not initialized');
        }
        if (this.n1Extractors.has(captureId)) {
            console.warn(`${this.LOG_PREFIX} N-1 encoder already running for ${captureId}`);
            return;
        }
        const captureTrack = this.audioPlayer.createN1Capture(captureId, excludedTrackIds);
        const extractor = createAudioFrameExtractor(captureTrack);
        if (extractor instanceof AudioFrameExtractor) {
            await AudioFrameExtractor.initialize(this.audioContext);
        }
        extractor.start();
        this.n1Extractors.set(captureId, extractor);
        const role = `n1:${captureId}`;
        const outputTrack = extractor.getOutputTrack();
        await this.audioEncoderWorkerProxy.addCompositeEncoder(role, codec, bitrate, channels, proxy(outputTrack), proxy(callback));
        console.log(`${this.LOG_PREFIX} N-1 encoder started for ${captureId}, excluding ${excludedTrackIds.join(', ') || '(none)'}`);
    }
    /** Replace the inbound tracks kept out of a running N-1 mix. */
    setN1ExcludedTracks(captureId, excludedTrackIds) {
        this.audioPlayer?.setN1ExcludedTracks(captureId, excludedTrackIds);
    }
    async stopN1Encoder(captureId) {
        const role = `n1:${captureId}`;
        await this.audioEncoderWorkerProxy?.removeCompositeEncoder(role);
        const extractor = this.n1Extractors.get(captureId);
        if (extractor) {
            extractor.stop();
            this.n1Extractors.delete(captureId);
        }
        this.audioPlayer?.removeN1Capture(captureId);
        console.log(`${this.LOG_PREFIX} N-1 encoder stopped for ${captureId}`);
    }
    async stopAllN1Encoders() {
        for (const captureId of [...this.n1Extractors.keys()]) {
            await this.stopN1Encoder(captureId);
        }
    }
    // ========== Content audio stop/resume (guest-side) ==========
    stopContentAudioTracks() {
        return this.audioPlayer?.stopContentAudioTracks() ?? [];
    }
    resumeContentAudioTracks() {
        this.audioPlayer?.resumeContentAudioTracks();
    }
    exemptTrackFromContentStop(trackId) {
        this.audioPlayer?.exemptTrackFromContentStop(trackId);
    }
    async destroyDecoderTrack(trackId) {
        await this.audioDecoderWorkerProxy?.destroyTrack(trackId);
        this.audioPlayer?.removeTrack(trackId);
    }
    // ========== Audio decoding & playback ==========
    async feedEncodedAudioFrame(frame) {
        let decoder = this.audioDecoders.get(frame.trackId);
        if (!decoder) {
            decoder = await createAudioDecoder(frame.trackId, frame.codec ?? Settings.audioCodec);
            await decoder.init((error) => {
                console.error(`${this.LOG_PREFIX} decoder error for track ${frame.trackId}:`, error);
            });
            decoder
                .getUnencodedOutputTrack()
                .setListener(async (unencodedAudioFrame, done) => {
                if (done) {
                    this.audioDecoders.delete(frame.trackId);
                    return;
                }
                if (unencodedAudioFrame) {
                    this.audioPlayer?.feedAudioPacket({
                        trackId: unencodedAudioFrame.trackId,
                        audioData: {
                            channels: [unencodedAudioFrame.data],
                            timestamp: unencodedAudioFrame.timestamp,
                            duration: unencodedAudioFrame.duration,
                        },
                        endOfTrack: unencodedAudioFrame.done,
                    });
                }
            });
            this.audioDecoders.set(frame.trackId, decoder);
        }
        if (!this.incomingTracks.has(frame.trackId)) {
            this.incomingTracks.set(frame.trackId, new IncomingTrack(frame.trackId, frame.userSessionId, async (f) => {
                if (f) {
                    decoder?.feedEncodedAudioFrame(f);
                }
            }));
        }
        this.incomingTracks.get(frame.trackId)?.feedFrame(frame, frame.done);
    }
    // ========== Playback controls ==========
    async resumeAllContexts() {
        this.audioPlayer?.flushAllTracks();
        await this.audioContext.resume();
    }
    async suspendAllContexts() {
        await this.audioContext.suspend();
    }
    setTrackMuted(trackId, muted) {
        if (this.inputAudioTracks.has(trackId)) {
            const g = muted ? 0 : 1;
            this.inputTrackGains.set(trackId, g);
            this.audioPlayer?.setInputSourceCaptureGain(trackId, g);
            return;
        }
        this.audioPlayer?.setTrackGain(trackId, muted ? 0 : 1);
    }
    setTrackGain(trackId, gain) {
        if (this.inputAudioTracks.has(trackId)) {
            this.inputTrackGains.set(trackId, gain);
            this.audioPlayer?.setInputSourceCaptureGain(trackId, gain);
            return;
        }
        this.audioPlayer?.setTrackGain(trackId, gain);
    }
    getTrackGain(trackId) {
        if (this.inputAudioTracks.has(trackId)) {
            return this.inputTrackGains.get(trackId) ?? 1;
        }
        return this.audioPlayer?.getTrackGain(trackId) ?? 1;
    }
    async handleLeave(userSessionId) {
        const trackIdsToDelete = [];
        for (const [trackId, track] of this.incomingTracks) {
            if (track.getUserSessionId() === userSessionId) {
                trackIdsToDelete.push(trackId);
            }
        }
        console.log(`${this.LOG_PREFIX} handleLeave`, {
            userSessionId,
            trackIdsToDelete,
        });
        for (const trackId of trackIdsToDelete) {
            this.incomingTracks.delete(trackId);
            const decoder = this.audioDecoders.get(trackId);
            if (decoder) {
                await decoder.destroy();
                this.audioDecoders.delete(trackId);
            }
            this.audioPlayer?.removeTrack(trackId);
        }
    }
    // ========== Worker creation ==========
    createAudioEncoderWorker() {
        if (import.meta.env.BUILD_TRANSFORMS_FILENAMES) {
            return new Worker(new URL('./AudioEncoderWorker.ts', import.meta.url), { type: 'module', name: perfWorkerName('audioEncoderWorker') });
        }
        const ext = import.meta.env.DEV ? '.ts' : '.js';
        return createWorker(`./AudioEncoderWorker${ext}`, import.meta.url, perfWorkerName('audioEncoderWorker'));
    }
    createAudioDecoderWorker() {
        if (import.meta.env.BUILD_TRANSFORMS_FILENAMES) {
            return new Worker(new URL('./AudioDecoderWorker.ts', import.meta.url), { type: 'module', name: perfWorkerName('audioDecoderWorker') });
        }
        const ext = import.meta.env.DEV ? '.ts' : '.js';
        return createWorker(`./AudioDecoderWorker${ext}`, import.meta.url, perfWorkerName('audioDecoderWorker'));
    }
}
